import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EVENT_KEYS, trackClick } from '../../services/api/trackingService';
import {
    ArrowRight,
    Plus,
    AlertCircle,
    Loader2,
    ChevronDown,
    ChevronRight,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
    CaseReport,
    DEFAULT_REPORT_SORT,
    deleteReport,
    getMyReports,
    getReportingFormData,
    ReportSortValue,
} from '../../services/reportService';
import { getDocumentSchema } from '../../services/documentSchemaService';
import {
    getLegacyCptReports,
    LegacyCptReport,
} from '../../services/legacyCptReportService';
import { DocumentSchema } from '../documents/types';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import ManageReportsCaseCard from './manage-reports/ManageReportsCaseCard';
import LegacyCptReportCard from './manage-reports/LegacyCptReportCard';
import ManageReportsFilters from './manage-reports/ManageReportsFilters';
import ManageReportsPagination from './manage-reports/ManageReportsPagination';

interface CreateCaseTileConfig {
    to: string;
    scenario: string;
    cptLabel: string;
    title: string;
    description: string;
    accentClass: string;
}

const CREATE_CASE_TILES: CreateCaseTileConfig[] = [
    {
        to: '/manage-reports/new?scenario=default',
        scenario: 'default',
        cptLabel: '76014 / 76015 workflow',
        title: 'Initial Evaluation',
        description:
            'Document implant research and timing for technologist assessment.',
        accentClass: 'bg-primary/20',
    },
    {
        to: '/manage-reports/new?scenario=risk-benefit-only&schemaKey=risk-benefit-analysis',
        scenario: 'risk-benefit-only',
        cptLabel: '76016 workflow',
        title: 'Risk Benefit Analysis',
        description:
            'Physician or QHP MR safety determination for non-routine cases.',
        accentClass: 'bg-emerald-500/20',
    },
    {
        to: '/manage-reports/new?scenario=device-preparation-only&schemaKey=device-preparation',
        scenario: 'device-preparation-only',
        cptLabel: '76018 workflow',
        title: 'Device Preparation',
        description:
            'Document implanted-device preparation performed on the day of the MRI exam.',
        accentClass: 'bg-amber-500/20',
    },
    {
        to: '/manage-reports/new?scenario=positioning-immobilization-only&schemaKey=positioning-immobilization',
        scenario: 'positioning-immobilization-only',
        cptLabel: '76019 workflow',
        title: 'Positioning / Immobilization',
        description:
            'Document implant-directed positioning or immobilization steps.',
        accentClass: 'bg-blue-500/20',
    },
];

const DEFAULT_CASES_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 300;

type StatusTab = 'all' | 'draft' | 'auto-saved' | 'completed';

const STATUS_TABS: { value: StatusTab; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'draft', label: 'Drafts' },
    { value: 'auto-saved', label: 'Auto-saved' },
    { value: 'completed', label: 'Completed' },
];

const ManageReports: React.FC = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [cases, setCases] = useState<CaseReport[]>([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(DEFAULT_CASES_LIMIT);
    const [totalCases, setTotalCases] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [sort, setSort] = useState<ReportSortValue>(DEFAULT_REPORT_SORT);
    const [cptCodes, setCptCodes] = useState<string[]>([]);
    const [statusTab, setStatusTab] = useState<StatusTab>('all');
    const [needsSignatureOnly, setNeedsSignatureOnly] = useState(false);
    const [isCasesLoading, setIsCasesLoading] = useState(true);
    const [isSupportingDataLoading, setIsSupportingDataLoading] =
        useState(true);
    const [casesError, setCasesError] = useState<string | null>(null);
    const [supportingDataError, setSupportingDataError] = useState<
        string | null
    >(null);
    const [deletingCaseId, setDeletingCaseId] = useState<string | null>(null);
    const [dataSources, setDataSources] = useState<Record<string, any>>({});
    const [legacyReports, setLegacyReports] = useState<LegacyCptReport[]>([]);
    const [isLegacyLoading, setIsLegacyLoading] = useState(true);
    const [legacyError, setLegacyError] = useState<string | null>(null);
    const [isLegacyExpanded, setIsLegacyExpanded] = useState(false);
    const [schemaMap, setSchemaMap] = useState<Record<string, DocumentSchema>>(
        {}
    );
    const latestCasesRequestRef = useRef(0);
    const normalizedSearchTerm = searchTerm.trim();
    const isSearchPending = normalizedSearchTerm !== debouncedSearchTerm;

    useEffect(() => {
        let isCancelled = false;

        const fetchSupportingData = async () => {
            setIsSupportingDataLoading(true);
            setSupportingDataError(null);

            try {
                const [
                    formData,
                    caseContextSchema,
                    initialEvaluationSchema,
                    riskBenefitSchema,
                    positioningImmobilizationSchema,
                    devicePreparationSchema,
                ] = await Promise.all([
                    getReportingFormData(),
                    getDocumentSchema('case-context'),
                    getDocumentSchema('initial-evaluation'),
                    getDocumentSchema('risk-benefit-analysis'),
                    getDocumentSchema('positioning-immobilization'),
                    getDocumentSchema('device-preparation'),
                ]);

                if (isCancelled) {
                    return;
                }

                setDataSources({
                    scanners: formData.department?.scanners || [],
                    departmentUsers: formData.department?.departmentUsers || [],
                });
                setSchemaMap({
                    'case-context': caseContextSchema.schema,
                    'initial-evaluation': initialEvaluationSchema.schema,
                    'risk-benefit-analysis': riskBenefitSchema.schema,
                    'positioning-immobilization':
                        positioningImmobilizationSchema.schema,
                    'device-preparation': devicePreparationSchema.schema,
                });
            } catch {
                if (isCancelled) {
                    return;
                }

                setDataSources({});
                setSchemaMap({});
                setSupportingDataError(
                    'Failed to load report configuration. Report previews may be incomplete.'
                );
            } finally {
                if (!isCancelled) {
                    setIsSupportingDataLoading(false);
                }
            }
        };

        fetchSupportingData();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const fetchLegacyReports = async () => {
            setIsLegacyLoading(true);
            setLegacyError(null);

            try {
                const legacyReportsData = await getLegacyCptReports();

                if (isCancelled) {
                    return;
                }

                setLegacyReports(legacyReportsData);
            } catch {
                if (isCancelled) {
                    return;
                }

                setLegacyReports([]);
                setLegacyError(
                    'Failed to load legacy CPT reports. Please try again.'
                );
            } finally {
                if (!isCancelled) {
                    setIsLegacyLoading(false);
                }
            }
        };

        fetchLegacyReports();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(normalizedSearchTerm);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [normalizedSearchTerm]);

    const fetchCases = useCallback(
        async (
            nextPage: number,
            nextSearch: string,
            nextSort: ReportSortValue,
            nextCptCodes: string[],
            nextStatusTab: StatusTab,
            nextNeedsSignatureOnly: boolean
        ) => {
            const requestId = latestCasesRequestRef.current + 1;
            latestCasesRequestRef.current = requestId;

            setIsCasesLoading(true);
            setCasesError(null);

            const filters: Record<string, string[]> = {};
            if (nextCptCodes.length > 0) {
                filters.cptCode = nextCptCodes;
            }
            if (nextStatusTab !== 'all') {
                filters.status = [nextStatusTab];
            }
            if (nextNeedsSignatureOnly) {
                filters.needsMySignature = ['true'];
            }

            try {
                const result = await getMyReports({
                    page: nextPage,
                    limit,
                    sort: nextSort,
                    ...(nextSearch ? { search: nextSearch } : {}),
                    ...(Object.keys(filters).length > 0 ? { filters } : {}),
                });

                if (requestId !== latestCasesRequestRef.current) {
                    return;
                }

                setCases(result.data);
                setTotalCases(result.total);
            } catch {
                if (requestId !== latestCasesRequestRef.current) {
                    return;
                }

                setCases([]);
                setTotalCases(0);
                setCasesError('Failed to load cases. Please try again.');
            } finally {
                if (requestId === latestCasesRequestRef.current) {
                    setIsCasesLoading(false);
                }
            }
        },
        [limit]
    );

    useEffect(() => {
        if (isSearchPending) {
            return;
        }

        void fetchCases(
            page,
            debouncedSearchTerm,
            sort,
            cptCodes,
            statusTab,
            needsSignatureOnly
        );
    }, [
        cptCodes,
        debouncedSearchTerm,
        fetchCases,
        isSearchPending,
        needsSignatureOnly,
        page,
        sort,
        statusTab,
    ]);

    const handleSearchChange = (value: string) => {
        const nextSearch = value.trim();

        setSearchTerm(value);
        setPage((currentPage) => (currentPage === 1 ? currentPage : 1));

        if (nextSearch !== debouncedSearchTerm) {
            latestCasesRequestRef.current += 1;
            setIsCasesLoading(true);
        }
    };

    const handleSortChange = (value: ReportSortValue) => {
        setSort(value);
        setPage((currentPage) => (currentPage === 1 ? currentPage : 1));
    };

    const handleCptCodesChange = (codes: string[]) => {
        setCptCodes(codes);
        setPage((currentPage) => (currentPage === 1 ? currentPage : 1));
    };

    const handleNeedsSignatureChange = (value: boolean) => {
        setNeedsSignatureOnly(value);
        setPage((currentPage) => (currentPage === 1 ? currentPage : 1));
    };

    const handleStatusTabChange = (value: string) => {
        const next =
            STATUS_TABS.find((tab) => tab.value === value)?.value ?? 'all';
        setStatusTab(next);
        setPage((currentPage) => (currentPage === 1 ? currentPage : 1));
    };

    const openCaseWorkspace = (caseItem: CaseReport, schemaKey?: string) => {
        const params = new URLSearchParams();
        params.set('scenario', 'default');
        if (schemaKey) {
            params.set('schemaKey', schemaKey);
        }
        navigate(
            `/manage-reports/${caseItem._id}${
                params.toString() ? `?${params.toString()}` : ''
            }`
        );
    };

    const handleDeleteCase = async (caseRecordId: string) => {
        try {
            setDeletingCaseId(caseRecordId);
            await deleteReport(caseRecordId);

            const nextPage = cases.length === 1 && page > 1 ? page - 1 : page;

            if (nextPage !== page) {
                setPage(nextPage);
            } else {
                await fetchCases(
                    nextPage,
                    debouncedSearchTerm,
                    sort,
                    cptCodes,
                    statusTab,
                    needsSignatureOnly
                );
            }
        } catch {
            toast({
                variant: 'destructive',
                title: 'Failed to delete case',
                description:
                    'Only cases with draft report documents can be deleted.',
            });
        } finally {
            setDeletingCaseId(null);
        }
    };

    const totalCasesText = useMemo(() => {
        const total = totalCases;
        return `${total} case${total === 1 ? '' : 's'}`;
    }, [totalCases]);

    const createCaseTileClassName = cn(
        'relative h-full overflow-hidden border shadow-sm transition-all duration-200',
        'hover:border-border hover:shadow-md'
    );
    const shouldRenderLegacySection =
        isLegacyLoading || !!legacyError || legacyReports.length > 0;
    const isLoading = isCasesLoading || isSupportingDataLoading;
    const hasActiveSearch = normalizedSearchTerm.length > 0;

    return (
        <div className="container mx-auto max-w-6xl space-y-6 px-4 py-6">
            <div className="space-y-2">
                <h1 className="text-[clamp(2rem,3vw,2.6rem)] font-semibold tracking-tight">
                    CPT Report Cases
                </h1>
                <p className="text-sm text-muted-foreground">
                    Manage your CPT reporting cases ({totalCasesText})
                </p>
            </div>

            <Alert className="border-amber-200/80 bg-amber-50 dark:border-amber-700/80 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="m-0">PHI-Free Zone</AlertTitle>
                <AlertDescription>
                    Do not enter patient names, MRN, DOB, or other identifiers.
                    Use Case IDs for tracking.
                </AlertDescription>
            </Alert>

            <main className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                    {CREATE_CASE_TILES.map((tile) => (
                        <Link
                            key={tile.scenario}
                            to={tile.to}
                            onClick={() =>
                                trackClick(EVENT_KEYS.clickCreateCpt, {
                                    scenario: tile.scenario,
                                })
                            }
                            className="group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                            <Card
                                className={cn(
                                    createCaseTileClassName,
                                    'border-border/80 bg-muted/20'
                                )}
                            >
                                <div
                                    className={cn(
                                        'absolute inset-x-0 top-0 h-px',
                                        tile.accentClass
                                    )}
                                />
                                <CardHeader className="relative px-6 pt-6 pb-11">
                                    <div className="flex min-w-0 items-start gap-4">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/15 bg-primary/[0.06] text-primary">
                                            <Plus className="h-5 w-5" />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                                                {tile.cptLabel}
                                            </div>
                                            <CardTitle className="text-xl leading-tight">
                                                {tile.title}
                                            </CardTitle>
                                            <CardDescription className="max-w-sm text-sm leading-relaxed text-muted-foreground">
                                                {tile.description}
                                            </CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <div className="pointer-events-none absolute right-5 bottom-5 text-muted-foreground transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-foreground">
                                    <ArrowRight className="h-4 w-4" />
                                </div>
                            </Card>
                        </Link>
                    ))}
                </div>

                <div className="space-y-6">
                    <Tabs
                        value={statusTab}
                        onValueChange={handleStatusTabChange}
                    >
                        <TabsList className="max-w-full overflow-x-auto">
                            {STATUS_TABS.map((tab) => (
                                <TabsTrigger key={tab.value} value={tab.value}>
                                    {tab.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>

                    <ManageReportsFilters
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        sort={sort}
                        onSortChange={handleSortChange}
                        cptCodes={cptCodes}
                        onCptCodesChange={handleCptCodesChange}
                        needsSignatureOnly={needsSignatureOnly}
                        onNeedsSignatureChange={handleNeedsSignatureChange}
                    />

                    {supportingDataError && (
                        <Alert variant="destructive" className="mb-6">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {supportingDataError}
                            </AlertDescription>
                        </Alert>
                    )}

                    {casesError && (
                        <Alert variant="destructive" className="mb-6">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{casesError}</AlertDescription>
                        </Alert>
                    )}

                    {isLoading ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-muted-foreground">
                                <Loader2 className="h-8 w-8 animate-spin" />
                                <p>Loading cases...</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {cases.map((caseItem) => {
                                return (
                                    <ManageReportsCaseCard
                                        key={caseItem._id}
                                        caseItem={caseItem}
                                        dataSources={dataSources}
                                        schemaMap={schemaMap}
                                        isDeleting={
                                            deletingCaseId === caseItem._id
                                        }
                                        onDeleteCase={handleDeleteCase}
                                        onOpenCase={openCaseWorkspace}
                                    />
                                );
                            })}

                            {!cases.length && (
                                <Card>
                                    <CardContent className="py-10 text-center text-sm text-muted-foreground">
                                        {hasActiveSearch
                                            ? 'No cases match your search.'
                                            : 'No cases found. Create a new case to get started.'}
                                    </CardContent>
                                </Card>
                            )}

                            {(totalCases > 0 || hasActiveSearch) && (
                                <ManageReportsPagination
                                    page={page}
                                    limit={limit}
                                    total={totalCases}
                                    isDisabled={
                                        isCasesLoading || isSearchPending
                                    }
                                    onPrevious={() =>
                                        setPage((currentPage) =>
                                            Math.max(currentPage - 1, 1)
                                        )
                                    }
                                    onNext={() =>
                                        setPage(
                                            (currentPage) => currentPage + 1
                                        )
                                    }
                                />
                            )}
                        </div>
                    )}
                </div>
                {shouldRenderLegacySection && (
                    <Card>
                        <CardHeader className="border-b pb-5">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="space-y-1">
                                    <CardTitle>Legacy CPT Reports</CardTitle>
                                    <CardDescription>
                                        View-only archive of CPT reports from
                                        legacy form.
                                    </CardDescription>
                                </div>

                                <button
                                    type="button"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    onClick={() =>
                                        setIsLegacyExpanded((prev) => !prev)
                                    }
                                >
                                    {isLegacyExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </CardHeader>
                        {isLegacyExpanded && (
                            <CardContent className="pt-6">
                                {legacyError && (
                                    <Alert
                                        variant="destructive"
                                        className="mb-6"
                                    >
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>
                                            {legacyError}
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {isLegacyLoading ? (
                                    <Card>
                                        <CardContent className="flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-muted-foreground">
                                            <Loader2 className="h-8 w-8 animate-spin" />
                                            <p>Loading legacy CPT reports...</p>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <div className="space-y-4">
                                        {legacyReports.map((report) => (
                                            <LegacyCptReportCard
                                                key={report._id}
                                                report={report}
                                            />
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        )}
                    </Card>
                )}
            </main>
        </div>
    );
};

export default ManageReports;
